import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Activity, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import BmrCalculationForm from './BmrCalculatorPage/components/BmrCalculationForm';
import ResultsOverview from './BmrCalculatorPage/components/ResultsOverview';
import NutritionalBreakdown from './BmrCalculatorPage/components/NutritionalBreakdown';
import DailyMealPlan from './BmrCalculatorPage/components/DailyMealPlan';
import QuickAdjustmentSidebar from './BmrCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './BmrCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './BmrCalculatorPage/components/TipsSidebar';
import InfoContent from './BmrCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  gender: 'female', // 'male' или 'female'
  age: '30',
  weight: '65',
  height: '165',
  activityLevel: 'light', // 'sedentary', 'light', 'moderate', 'active', 'very_active'
  formula: 'mifflin', // 'mifflin', 'harris', 'katch'
  bodyFat: '', // Для формулы Кэтча-МакАрдла (опционально)
  goal: 'maintain', // 'lose', 'maintain', 'gain'
  weightGoal: '0.5', // Сколько кг в неделю (для похудения/набора)
  mealCount: '3', // Количество приемов пищи в день
  includeSnacks: true,
  dietType: 'balanced', // 'balanced', 'low_carb', 'high_protein', 'keto'
};

// Определение уровней активности
export const activityLevels = [
  { id: 'sedentary', name: 'Сидячий образ жизни', factor: 1.2, description: 'Минимальная или отсутствие физической активности' },
  { id: 'light', name: 'Легкая активность', factor: 1.375, description: 'Легкие упражнения/спорт 1-3 дня в неделю' },
  { id: 'moderate', name: 'Умеренная активность', factor: 1.55, description: 'Умеренные упражнения/спорт 3-5 дней в неделю' },
  { id: 'active', name: 'Высокая активность', factor: 1.725, description: 'Интенсивные упражнения/спорт 6-7 дней в неделю' },
  { id: 'very_active', name: 'Очень высокая активность', factor: 1.9, description: 'Тяжелая физическая работа или тренировки 2 раза в день' }
];

// Определение формул расчета БМР
export const bmrFormulas = [
  { id: 'mifflin', name: 'Миффлина-Сан Жеора', description: 'Современная формула, наиболее точная для большинства людей' },
  { id: 'harris', name: 'Харриса-Бенедикта', description: 'Классическая формула, используется с 1919 года' },
  { id: 'katch', name: 'Кэтча-МакАрдла', description: 'Учитывает процент жира в организме (требуется дополнительный замер)' }
];

// Определение типов диет
export const dietTypes = [
  { id: 'balanced', name: 'Сбалансированное питание', macro: { protein: 0.3, fat: 0.3, carbs: 0.4 }, description: 'Классическое распределение с упором на разнообразие продуктов' },
  { id: 'low_carb', name: 'Низкоуглеводная', macro: { protein: 0.35, fat: 0.45, carbs: 0.2 }, description: 'Меньше углеводов, больше белков и полезных жиров' },
  { id: 'high_protein', name: 'Высокобелковая', macro: { protein: 0.4, fat: 0.3, carbs: 0.3 }, description: 'Повышенное содержание белка для наращивания мышечной массы' },
  { id: 'keto', name: 'Кетогенная', macro: { protein: 0.25, fat: 0.7, carbs: 0.05 }, description: 'Минимум углеводов, максимум жиров для кетоза' }
];

/**
 * BmrCalculatorPage Component - Калькулятор базального метаболизма с полной аналитикой потребностей в питании.
 */
const BmrCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('bmr-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор базального метаболизма (BMR) | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте свой базальный метаболизм (BMR) и дневную норму калорий (TDEE) с учетом активности, целей похудения или набора массы, а также получите план питания с распределением белков, жиров и углеводов.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [showMealPlan, setShowMealPlan] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных профилей из localStorage ---
  useEffect(() => {
    const savedProfilesData = localStorage.getItem('savedBmrProfiles');
    if (savedProfilesData) {
      try {
        setSavedProfiles(JSON.parse(savedProfilesData));
      } catch (e) {
        console.error("Error loading saved profiles:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'weight-loss':
          newState.goal = 'lose';
          break;
        case 'weight-gain':
          newState.goal = 'gain';
          break;
        case 'athletes':
          newState.activityLevel = 'active';
          newState.dietType = 'high_protein';
          break;
        case 'keto':
          newState.dietType = 'keto';
          break;
        case 'seniors':
          newState.age = '65';
          newState.activityLevel = 'light';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});
    setShowMealPlan(false);

  }, [subToolId]);

  // Сохранение профиля в localStorage
  const saveProfile = (name) => {
    if (!results) return;

    const newSavedProfile = {
      id: Date.now().toString(),
      name: name || `Профиль от ${new Date().toLocaleDateString()}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProfiles = [...savedProfiles, newSavedProfile];
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedBmrProfiles', JSON.stringify(updatedSavedProfiles));

    return newSavedProfile;
  };

  // Удаление сохраненного профиля
  const deleteSavedProfile = (id) => {
    const updatedSavedProfiles = savedProfiles.filter(profile => profile.id !== id);
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedBmrProfiles', JSON.stringify(updatedSavedProfiles));
  };

  // Загрузка сохраненного профиля
  const loadSavedProfile = (id) => {
    const profileToLoad = savedProfiles.find(profile => profile.id === id);
    if (profileToLoad) {
      setFormData(profileToLoad.formData);
      setResults(profileToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Activity className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор базального метаболизма (BMR)'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <BmrCalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              activityLevels={activityLevels}
              bmrFormulas={bmrFormulas}
              dietTypes={dietTypes}
              saveProfile={saveProfile}
              setShowMealPlan={setShowMealPlan}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <NutritionalBreakdown 
                  results={results} 
                  formData={formData}
                  dietTypes={dietTypes}
                />
                {showMealPlan && (
                  <DailyMealPlan 
                    results={results}
                    formData={formData}
                    dietTypes={dietTypes}
                  />
                )}
              </>
            )}

            {/* --- Информационный контент о метаболизме --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения профилей --- */}
            <ComparisonSidebar 
              savedProfiles={savedProfiles}
              deleteSavedProfile={deleteSavedProfile}
              loadSavedProfile={loadSavedProfile}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default BmrCalculatorPage;